import styled, { css } from "styled-components";
import { Link } from "react-router-dom";
import { theme, typography } from "../Homepage/styles";

/** Lovable glass-card: bg-card/80 backdrop-blur-xl */
const glassCard = css`
  border: 1px solid hsl(var(--hp-border) / 0.5);
  border-radius: 1rem;
  background: hsl(var(--hp-card) / 0.8);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  box-shadow: ${theme.shadowCard};
`;

/** Override global shards a:hover (blue + underline). */
const linkNoUnderline = css`
  &,
  &:hover,
  &:focus,
  &:active,
  &:visited {
    text-decoration: none;
  }
`;

export const ProfilePage = styled.div`
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  background: ${theme.bg};
  color: ${theme.foreground};
  font-family: ${typography.fontFamily};
`;

export const ProfileGlow = styled.div`
  pointer-events: none;
  position: absolute;
  inset: 0 0 auto;
  height: 420px;
  background-image: ${theme.gradientHero};
`;

export const ProfileShell = styled.div`
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  padding-top: 64px;
`;

export const ProfileMain = styled.main`
  flex: 1;
  padding: 40px 16px 64px;

  @media (min-width: 640px) {
    padding: 56px 16px 72px;
  }
`;

export const ProfileInner = styled.div`
  width: 100%;
  max-width: 48rem;
  margin: 0 auto;
`;

export const ProfileHero = styled.div`
  ${glassCard};
  padding: 24px;

  @media (min-width: 640px) {
    padding: 32px;
  }
`;

export const ProfileHeroRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (min-width: 640px) {
    flex-direction: row;
    align-items: center;
  }
`;

export const ProfileAvatarWrap = styled.div`
  position: relative;
  align-self: flex-start;
  width: 96px;
  height: 96px;
  flex-shrink: 0;
`;

export const ProfileAvatarImg = styled.img`
  display: block;
  width: 96px;
  height: 96px;
  border-radius: 9999px;
  object-fit: cover;
  border: 2px solid hsl(var(--hp-border) / 0.6);
  background: ${theme.muted};
`;

export const ProfileAvatarFallback = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 96px;
  height: 96px;
  border-radius: 9999px;
  border: 2px solid hsl(var(--hp-border) / 0.6);
  background: hsl(var(--hp-primary) / 0.15);
  color: ${theme.primary};
  font-size: 1.5rem;
  font-weight: ${typography.weights.bold};
`;

export const ProfileAvatarEdit = styled.button`
  position: absolute;
  right: -2px;
  bottom: -2px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 9999px;
  background: ${theme.primary};
  color: ${theme.primaryForeground};
  box-shadow: ${theme.shadowButton};
  cursor: pointer;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    opacity: 0.92;
  }
`;

export const ProfileHeroText = styled.div`
  min-width: 0;
`;

export const ProfileName = styled.h1`
  margin: 0;
  color: ${theme.foreground};
  font-size: 1.5rem;
  font-weight: ${typography.weights.bold};
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (min-width: 640px) {
    font-size: 1.875rem;
  }
`;

export const ProfileEmail = styled.p`
  margin: 4px 0 0;
  color: ${theme.mutedForeground};
  font-size: 0.9375rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ProfileOutlineBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  min-height: 2.25rem;
  padding: 0 12px;
  border: 1px solid hsl(var(--hp-border) / 0.8);
  border-radius: 0.5rem;
  background: transparent;
  color: ${theme.foreground};
  font-family: ${typography.fontFamily};
  font-size: 0.8125rem;
  font-weight: ${typography.weights.medium};
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background: hsl(var(--hp-muted) / 0.5);
  }
`;

export const ProfileTabList = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 32px;
  padding: 4px;
  overflow-x: auto;
  border: 1px solid hsl(var(--hp-border) / 0.5);
  border-radius: 0.75rem;
  /* Lovable Profile tabs: bg-card/60 backdrop-blur-xl */
  background: hsl(var(--hp-card) / 0.6);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const ProfileTab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  padding: 10px 16px;
  border: none;
  border-radius: 0.5rem;
  background: transparent;
  color: ${theme.mutedForeground};
  font-family: ${typography.fontFamily};
  font-size: 0.875rem;
  font-weight: ${typography.weights.medium};
  white-space: nowrap;
  cursor: pointer;
  transition: color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  &[aria-selected="true"] {
    background: ${theme.primary};
    color: ${theme.primaryForeground};
    box-shadow: ${theme.shadowButton};
  }

  &:hover:not([aria-selected="true"]) {
    color: ${theme.foreground};
    background: hsl(var(--hp-muted) / 0.5);
  }
`;

export const ProfilePanel = styled.div`
  margin-top: 24px;
`;

export const ProfileCard = styled.div`
  ${glassCard};
  padding: 24px;

  @media (min-width: 640px) {
    padding: 32px;
  }
`;

export const ProfileCardHead = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

export const ProfileCardIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 0.75rem;
  background: hsl(var(--hp-primary) / 0.15);
  color: ${theme.primary};

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const ProfileCardTitle = styled.h2`
  margin: 0;
  color: ${theme.foreground};
  font-size: 1.125rem;
  font-weight: ${typography.weights.semibold};
  line-height: 1.3;
`;

export const ProfileCardDesc = styled.p`
  margin: 4px 0 0;
  color: ${theme.mutedForeground};
  font-size: 0.875rem;
  line-height: 1.5;
`;

export const ProfileFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 24px;
`;

export const ProfileField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ProfileLabel = styled.label`
  color: ${theme.foreground};
  font-size: 0.875rem;
  font-weight: ${typography.weights.medium};
`;

export const ProfileInput = styled.input`
  width: 100%;
  min-height: 2.75rem;
  padding: 0 14px;
  border: 1px solid hsl(var(--hp-border) / 0.6);
  border-radius: 0.5rem;
  background: hsl(var(--hp-background) / 0.5);
  color: ${theme.foreground};
  font-family: ${typography.fontFamily};
  font-size: 0.9375rem;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${theme.primary};
    box-shadow: 0 0 0 3px hsl(var(--hp-primary) / 0.15);
  }

  &:disabled {
    background: hsl(var(--hp-muted) / 0.4);
    color: ${theme.mutedForeground};
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${theme.mutedForeground};
  }
`;

export const ProfileHint = styled.p`
  margin: 0;
  color: ${theme.mutedForeground};
  font-size: 0.75rem;
  line-height: 1.4;
`;

export const ProfileAlert = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 24px;
  padding: 12px 14px;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  line-height: 1.45;

  svg {
    width: 16px;
    height: 16px;
    margin-top: 2px;
    flex-shrink: 0;
  }

  ${(p) =>
    p.$variant === "error"
      ? css`
          border: 1px solid hsl(0 72% 51% / 0.35);
          background: hsl(0 72% 51% / 0.1);
          color: hsl(0 65% 40%);
        `
      : css`
          border: 1px solid hsl(var(--hp-primary) / 0.4);
          background: hsl(var(--hp-primary) / 0.1);
          color: ${theme.foreground};

          svg {
            color: ${theme.primary};
          }
        `}
`;

export const ProfileFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 32px;
`;

export const ProfileGhostBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.75rem;
  padding: 0 16px;
  border: none;
  border-radius: 0.5rem;
  background: transparent;
  color: ${theme.mutedForeground};
  font-family: ${typography.fontFamily};
  font-size: 0.875rem;
  font-weight: ${typography.weights.medium};
  cursor: pointer;

  &:hover:not(:disabled) {
    color: ${theme.foreground};
    background: hsl(var(--hp-muted) / 0.5);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const ProfilePrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 9rem;
  min-height: 2.75rem;
  padding: 0 20px;
  border: none;
  border-radius: 0.5rem;
  background-image: ${theme.gradientGold};
  box-shadow: ${theme.shadowButton};
  color: ${theme.primaryForeground};
  font-family: ${typography.fontFamily};
  font-size: 0.875rem;
  font-weight: ${typography.weights.bold};
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover:not(:disabled) {
    transform: scale(1.02);
  }

  &:disabled {
    opacity: 0.55;
    cursor: default;
    pointer-events: none;
  }
`;

export const ProfileCtaLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 2.5rem;
  padding: 0 18px;
  border-radius: 0.5rem;
  background-image: ${theme.gradientGold};
  box-shadow: ${theme.shadowButton};
  color: ${theme.primaryForeground};
  font-family: ${typography.fontFamily};
  font-size: 0.875rem;
  font-weight: ${typography.weights.bold};
  ${linkNoUnderline}

  &,
  &:hover,
  &:focus,
  &:active,
  &:visited {
    color: ${theme.primaryForeground};
  }

  &:hover {
    transform: scale(1.02);
  }
`;

export const ProfileHomeWrap = styled.div`
  margin-top: 32px;
  text-align: center;
`;

export const ProfileHomeLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${theme.mutedForeground};
  font-size: 0.875rem;
  ${linkNoUnderline}

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover,
  &:focus {
    color: ${theme.foreground};
  }
`;

/* Avatar modal */
export const ProfileModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: hsl(230 25% 5% / 0.55);
`;

export const ProfileModal = styled.div`
  width: 100%;
  max-width: 28rem;
  ${glassCard};
  padding: 24px;
`;

export const ProfileModalTitle = styled.h2`
  margin: 0;
  color: ${theme.foreground};
  font-size: 1.125rem;
  font-weight: ${typography.weights.semibold};
`;

export const ProfileModalDesc = styled.p`
  margin: 8px 0 0;
  color: ${theme.mutedForeground};
  font-size: 0.875rem;
  line-height: 1.5;
`;

export const ProfileDropzone = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  margin-top: 20px;
  min-height: 180px;
  padding: 24px;
  border: 2px dashed hsl(var(--hp-border) / 0.8);
  border-radius: 0.75rem;
  background: hsl(var(--hp-muted) / 0.2);
  color: ${theme.foreground};
  cursor: pointer;
  font-family: ${typography.fontFamily};

  &:hover {
    border-color: hsl(var(--hp-primary) / 0.6);
    background: hsl(var(--hp-muted) / 0.4);
  }
`;

export const ProfileCropWrap = styled.div`
  margin-top: 20px;
  border-radius: 0.75rem;
  overflow: hidden;
  background: #131722;

  .ReactCrop {
    max-height: 360px;
  }

  .ReactCrop__image {
    max-height: 360px;
    object-fit: contain;
  }
`;

export const ProfileModalActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
`;

export const ProfileHiddenFile = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

/* Favorites */
export const FavIntro = styled.div`
  margin-bottom: 8px;
  padding: 0 4px;
`;

export const FavStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const FavPanelHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

export const FavPanelTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  /* Lovable: h-9 w-9 icon box, h-4.5 icon */
  ${ProfileCardIcon} {
    width: 36px;
    height: 36px;
    border-radius: 0.5rem;

    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

export const FavCount = styled.span`
  border-radius: 9999px;
  background: hsl(var(--hp-primary) / 0.15);
  color: ${theme.primary};
  padding: 2px 10px;
  font-size: 0.75rem; /* text-xs */
  font-weight: ${typography.weights.semibold};
  line-height: 1.25;
`;

export const FavList = styled.ul`
  list-style: none;
  margin: 24px 0 0; /* mt-6 */
  padding: 0;
`;

export const FavItem = styled.li`
  display: flex;
  align-items: center;
  gap: 12px; /* gap-3 */
  padding: 14px 0; /* py-3.5 */
  border-bottom: 1px solid hsl(var(--hp-border) / 0.6);

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    padding-bottom: 0;
    border-bottom: none;
  }
`;

export const FavStarBtn = styled.button`
  display: inline-flex;
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: transparent;
  color: ${theme.primary};
  cursor: pointer;
  line-height: 0;

  svg {
    width: 18px; /* h-4.5 */
    height: 18px;
  }

  &:hover {
    color: ${theme.mutedForeground};
  }
`;

export const FavLink = styled(Link)`
  display: flex;
  flex: 1;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: inherit;
  ${linkNoUnderline}

  &,
  &:hover,
  &:focus,
  &:active,
  &:visited {
    color: inherit;
  }

  &:hover span:first-child {
    color: ${theme.primary};
  }
`;

export const FavTitle = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${theme.foreground};
  font-size: 0.875rem; /* text-sm */
  font-weight: ${typography.weights.medium}; /* font-medium */
  line-height: 1.25;
`;

export const FavMeta = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 8px;
  color: ${theme.mutedForeground};
  font-size: 0.75rem; /* text-xs */
  font-weight: ${typography.weights.normal};
  line-height: 1.25;

  svg {
    width: 16px; /* h-4 */
    height: 16px;
    opacity: 0.7;
  }
`;

export const FavEmpty = styled.div`
  margin-top: 32px;
  padding: 24px 0;
  text-align: center;
  color: ${theme.mutedForeground};
  font-size: 0.875rem; /* text-sm */
`;

export const FavLoading = styled.div`
  ${glassCard};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 56px 24px;
  color: ${theme.mutedForeground};
  font-size: 0.875rem;

  svg {
    width: 24px;
    height: 24px;
    color: ${theme.primary};
    animation: profile-spin 0.8s linear infinite;
  }

  @keyframes profile-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

/* Notifications */
export const NotifSections = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px; /* space-y-8 */
  margin-top: 32px;
`;

export const NotifSectionHead = styled.div`
  margin-bottom: 16px; /* mb-4 */
`;

export const NotifSectionTitle = styled.h3`
  margin: 0;
  color: ${theme.foreground};
  font-size: 1rem; /* text-base */
  font-weight: ${typography.weights.semibold};
  line-height: 1.4;
`;

export const NotifSectionDesc = styled.p`
  margin: 4px 0 0;
  color: ${theme.mutedForeground};
  font-size: 0.875rem; /* text-sm */
  line-height: 1.5;
`;

export const NotifTable = styled.div`
  border: 1px solid hsl(var(--hp-border) / 0.6);
  border-radius: 0.75rem; /* rounded-xl */
  overflow: hidden;
`;

export const NotifHead = styled.div`
  display: none;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
  padding: 12px 16px;
  border-bottom: 1px solid hsl(var(--hp-border) / 0.6);
  background: hsl(var(--hp-muted) / 0.3);
  color: ${theme.mutedForeground};
  font-size: 0.75rem; /* text-xs */
  font-weight: ${typography.weights.medium};

  @media (min-width: 640px) {
    display: grid;
  }
`;

export const NotifRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  padding: 16px;
  border-bottom: 1px solid hsl(var(--hp-border) / 0.6);
  align-items: center;

  &:last-child {
    border-bottom: none;
  }

  @media (min-width: 640px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;

export const NotifSpecialList = styled.div`
  border: 1px solid hsl(var(--hp-border) / 0.6);
  border-radius: 0.75rem;
  overflow: hidden;
`;

export const NotifSpecialRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border-bottom: 1px solid hsl(var(--hp-border) / 0.6);

  &:last-child {
    border-bottom: none;
  }

  @media (min-width: 640px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

export const NotifSpecialText = styled.div`
  min-width: 0;
`;

export const NotifSpecialTitle = styled.h4`
  margin: 0;
  color: ${theme.foreground};
  font-size: 0.875rem; /* text-sm */
  font-weight: ${typography.weights.medium};
  line-height: 1.4;
`;

export const NotifSpecialDesc = styled.p`
  margin: 2px 0 0;
  color: ${theme.mutedForeground};
  font-size: 0.75rem; /* text-xs */
  line-height: 1.45;
`;

export const NotifAnalyst = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;

  img {
    width: 36px; /* h-9 */
    height: 36px;
    border-radius: 9999px;
    object-fit: cover;
    border: 1px solid hsl(var(--hp-border) / 0.6);
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${theme.foreground};
    font-size: 0.875rem;
    font-weight: ${typography.weights.medium};
  }
`;

export const NotifToggleGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;

  @media (min-width: 640px) {
    justify-content: center;
  }
`;

export const NotifMobileLabel = styled.span`
  color: ${theme.mutedForeground};
  font-size: 0.875rem;

  @media (min-width: 640px) {
    display: none;
  }
`;

export const NotifToggles = styled.div`
  display: flex;
  align-items: center;
  gap: 20px; /* gap-5 */
  flex-shrink: 0;
`;

export const NotifToggle = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  margin: 0;
  font-size: 0.75rem; /* text-xs */
  font-weight: ${typography.weights.medium};
  color: ${theme.mutedForeground};
  user-select: none;
`;

/** Lovable / shadcn Switch: h-6 w-11, thumb h-5
 *  on  → primary track, thumb = background (dark on orange / white on orange in light)
 *  off → input/border track, thumb = background (recessed dark knob in dark mode)
 */
export const NotifSwitch = styled.button`
  position: relative;
  flex-shrink: 0;
  width: 44px; /* w-11 */
  height: 24px; /* h-6 */
  padding: 0;
  border: 2px solid transparent;
  border-radius: 9999px;
  background: ${({ $on }) => ($on ? theme.primary : theme.border)};
  cursor: pointer;
  transition: background 0.2s ease;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: ${({ $on }) => ($on ? "20px" : "0")}; /* translate-x-5 */
    width: 20px; /* h-5 w-5 */
    height: 20px;
    border-radius: 50%;
    /* Lovable: bg-background — dark knob when off in dark mode */
    background: ${theme.bg};
    box-shadow:
      0 1px 2px hsl(0 0% 0% / 0.2),
      0 1px 3px hsl(0 0% 0% / 0.12);
    transition: left 0.2s ease;
  }

  &:focus-visible {
    outline: 2px solid ${theme.primary};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
`;

export const NotifLoading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 28px 16px;
  color: ${theme.mutedForeground};
  font-size: 0.875rem;

  svg {
    width: 20px;
    height: 20px;
    color: ${theme.primary};
    animation: notif-spin 0.8s linear infinite;
  }

  @keyframes notif-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
